<template>
	<div class="admin">
		<div class="admin__header">
			<h1>Blog Admin</h1>
			<button @click="router.push({ name: 'BlogAdminForm' })">
				+ Add new blog
			</button>
		</div>

		<div v-if="loading" class="admin__state">Loading…</div>
		<div v-else-if="error" class="admin__state admin__state--error">
			Failed to load blogs.
		</div>

		<template v-else>
			<table class="admin__table">
				<thead>
					<tr>
						<th>Title</th>
						<th>Date</th>
						<th></th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="blog in blogs" :key="blog.link">
						<td>
							<a
								:href="`/blog/${blog.link}`"
								target="_blank"
								rel="noopener noreferrer"
								style="text-decoration: underline; color: #fff"
							>
								{{ blog.title }}
							</a>
						</td>
						<td>{{ formatDate(blog.createdDate) }}</td>
						<td>
							<button
								@click="
									router.push({
										name: 'BlogAdminFormEdit',
										params: { link: blog.link },
									})
								"
							>
								Edit
							</button>
						</td>
					</tr>
				</tbody>
			</table>

			<div v-if="totalPages > 1" class="admin__pagination">
				<button
					:disabled="currentPage === 1"
					@click="goToPage(currentPage - 1)"
				>
					←
				</button>
				<button
					v-for="p in totalPages"
					:key="p"
					:class="{ active: p === currentPage }"
					@click="goToPage(p)"
				>
					{{ p }}
				</button>
				<button
					:disabled="currentPage === totalPages"
					@click="goToPage(currentPage + 1)"
				>
					→
				</button>
			</div>
		</template>
	</div>
</template>

<script setup>
import { useRouter } from "vue-router";
import dayjs from "dayjs";
import api from "@/api/index.js";

const router = useRouter();

const PER_PAGE = 10;
const blogs = ref([]);
const loading = ref(false);
const error = ref(false);
const currentPage = ref(1);
const totalPages = ref(1);

let sessionInterval = null;

const formatDate = (d) => dayjs(d).format("MMM D, YYYY");

async function fetchBlogs(page) {
	loading.value = true;
	error.value = false;
	try {
		const res = await api.getBlogs({ page, perPage: PER_PAGE });
		blogs.value = res.data.items ?? res.data;
		totalPages.value = res.data.totalPages ?? 1;
	} catch {
		error.value = true;
	} finally {
		loading.value = false;
	}
}

function goToPage(page) {
	if (page < 1 || page > totalPages.value) return;
	currentPage.value = page;
	fetchBlogs(page);
}

async function checkSession() {
	try {
		await api.checkAdminSession();
	} catch {
		clearInterval(sessionInterval);
		localStorage.removeItem("sid");
		sessionStorage.removeItem("sid");
		router.push({ name: "BlogAdminLogin" });
	}
}

onMounted(() => {
	const sid = localStorage.getItem("sid");
	if (sid) sessionStorage.sid = sid;
	fetchBlogs(1);
	sessionInterval = setInterval(checkSession, 30_000);
});

onUnmounted(() => clearInterval(sessionInterval));
</script>

<style scoped lang="scss">
.admin {
	max-width: 900px;
	margin: 0 auto;
	padding: 40px 20px;
}

.admin__header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 24px;

	h1 {
		margin: 0;
		font-size: 24px;
	}

	button {
		padding: 8px 16px;
		background: #142c3a;
		color: #fff;
		border: none;
		border-radius: 4px;
		font-size: 14px;
		font-weight: 600;
		cursor: pointer;
	}
}

.admin__state {
	padding: 40px 0;
	text-align: center;
	color: #5a6a78;

	&--error {
		color: #c0392b;
	}
}

.admin__table {
	width: 100%;
	border-collapse: collapse;
	font-size: 14px;

	th,
	td {
		text-align: left;
		padding: 10px 12px;
		border-bottom: 1px solid #e0e6ed;
	}

	th {
		font-weight: 600;
		background: #0d1015;
	}

	tr:hover td {
		background: color-mix(in oklab, #0d1015, white 30%);
	}

	td:last-child {
		text-align: right;
	}

	button {
		padding: 4px 12px;
		background: #fff;
		border: 1px solid #ccc;
		border-radius: 4px;
		font-size: 13px;
		cursor: pointer;

		&:hover {
			background: color-mix(in oklab, #0d1015, white 30%);
		}
	}
}

.admin__pagination {
	display: flex;
	gap: 6px;
	margin-top: 20px;

	button {
		min-width: 34px;
		height: 34px;
		padding: 0 8px;
		border: 1px solid #ccc;
		background: #fff;
		border-radius: 4px;
		font-size: 13px;
		cursor: pointer;

		&.active {
			background: #142c3a;
			color: #fff;
			border-color: #142c3a;
		}
		&:disabled {
			opacity: 0.35;
			cursor: default;
		}
	}
}
</style>
